import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BarChart3, BrainCircuit, Download, FileUp, LogOut, PlayCircle } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, clearAuthSession, downloadProtectedFile } from "@/lib/portalApi";

const tabs = ["Resources", "Videos", "Assignments", "AI Helper"];

const buildChartData = (review) => {
  if (!review?.stats) return [];
  return [
    { name: "Your Score", value: review.score },
    { name: "Cohort Avg", value: review.stats.average },
    { name: "Highest", value: review.stats.highest },
  ];
};

export const StudentDashboard = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [resources, setResources] = useState([]);
  const [videos, setVideos] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [activeTab, setActiveTab] = useState("Resources");
  const [uploadMap, setUploadMap] = useState({});
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiAnswer, setAiAnswer] = useState("");
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);

  const submissionMap = useMemo(
    () => Object.fromEntries(submissions.map((submission) => [submission.assignment_id, submission])),
    [submissions],
  );

  const loadAll = async () => {
    try {
      const [meRes, resourcesRes, videosRes, assignmentsRes, submissionsRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/resources"),
        api.get("/videos"),
        api.get("/assignments"),
        api.get("/student/submissions"),
      ]);

      if (meRes.data.role !== "student") {
        navigate("/platform");
        return;
      }

      setMe(meRes.data);
      setResources(resourcesRes.data);
      setVideos(videosRes.data);
      setAssignments(assignmentsRes.data);
      setSubmissions(submissionsRes.data);
    } catch {
      clearAuthSession();
      navigate("/platform");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    clearAuthSession();
    navigate("/platform");
  };

  const handleAssignmentUpload = async (assignmentId) => {
    const files = uploadMap[assignmentId];
    if (!files?.length) {
      toast.error("Please choose at least one file");
      return;
    }

    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append("files", file));

    try {
      await api.post(`/assignments/${assignmentId}/submit`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Submission uploaded");
      loadAll();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to upload submission");
    }
  };

  const handleAskAi = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const response = await api.post("/ai/explain", {
        subject: me?.subjects?.[0] || "VCE Mathematical Methods",
        prompt: aiPrompt,
      });
      setAiAnswer(response.data.answer);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to get AI explanation");
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return <div className="ascend-container py-20" data-testid="student-dashboard-loading">Loading student portal...</div>;
  }

  return (
    <div className="min-h-screen bg-[#f8f7f2]" data-testid="student-dashboard-page">
      <div className="ascend-container py-10">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="eyebrow text-[#002147]/60" data-testid="student-dashboard-overline">Ascend Academy Platform</p>
            <h1 className="font-display text-4xl text-[#002147]" data-testid="student-dashboard-heading">Welcome, {me?.full_name}</h1>
            <p className="mt-2 text-sm text-slate-700" data-testid="student-dashboard-subjects">Subjects: {me?.subjects?.join(" · ")}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline" className="rounded-sm border-[#002147]/15 bg-white hover:bg-[#f4efe5]" data-testid="student-dashboard-site-link">
              <Link to="/">Main Website</Link>
            </Button>
            <Button onClick={handleLogout} className="rounded-sm bg-[#002147] text-white hover:bg-[#001530]" data-testid="student-dashboard-logout-button">
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>

        <div className="mb-8 grid gap-4 md:grid-cols-4" data-testid="student-dashboard-tabs">
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`border px-4 py-3 text-left text-sm font-semibold transition-colors ${activeTab === tab ? "bg-[#002147] text-white" : "bg-white text-[#173458]"}`}
              data-testid={`student-tab-${tab.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === "Resources" ? (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="student-resources-section">
            {resources.map((resource) => (
              <Card key={resource.id} className="section-frame rounded-sm bg-white shadow-none" data-testid={`student-resource-card-${resource.id}`}>
                <CardContent className="space-y-4 p-6">
                  <p className="text-xs uppercase tracking-[0.18em] text-[#002147]/55">{resource.resource_type}</p>
                  <h2 className="font-display text-2xl text-[#002147]">{resource.title}</h2>
                  <p className="text-sm leading-7 text-slate-700">{resource.description}</p>
                  <p className="text-sm text-slate-500">{resource.subject} · {resource.unit}</p>
                  {resource.file ? (
                    <Button type="button" variant="outline" className="rounded-sm border-[#002147]/15 bg-white hover:bg-[#f4efe5]" onClick={() => downloadProtectedFile(resource.file.download_url, resource.file.name)} data-testid={`student-resource-download-${resource.id}`}>
                      <Download className="mr-2 h-4 w-4" /> Download {resource.file.name}
                    </Button>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : null}

        {activeTab === "Videos" ? (
          <div className="grid gap-6 xl:grid-cols-2" data-testid="student-videos-section">
            {videos.map((video) => (
              <Card key={video.id} className="section-frame rounded-sm bg-white shadow-none" data-testid={`student-video-card-${video.id}`}>
                <CardContent className="space-y-4 p-6">
                  <p className="text-xs uppercase tracking-[0.18em] text-[#002147]/55">{video.category}</p>
                  <h2 className="font-display text-2xl text-[#002147]">{video.title}</h2>
                  <p className="text-sm leading-7 text-slate-700">{video.description}</p>
                  <div className="aspect-video overflow-hidden border border-[#002147]/10" data-testid={`student-video-frame-${video.id}`}>
                    <iframe title={video.title} src={video.embed_url} className="h-full w-full" allowFullScreen />
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600"><PlayCircle className="h-4 w-4" /> {video.subject}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : null}

        {activeTab === "Assignments" ? (
          <div className="space-y-6" data-testid="student-assignments-section">
            {assignments.map((assignment) => {
              const submission = submissionMap[assignment.id];
              const reviewed = submission?.status === "reviewed";
              const chartData = buildChartData(submission?.review);

              return (
                <Card key={assignment.id} className="section-frame rounded-sm bg-white shadow-none" data-testid={`student-assignment-card-${assignment.id}`}>
                  <CardContent className="space-y-5 p-6">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-[#002147]/55">{assignment.assignment_type}</p>
                        <h2 className="font-display text-3xl text-[#002147]">{assignment.title}</h2>
                        <p className="mt-2 text-sm leading-7 text-slate-700">{assignment.description}</p>
                        <p className="mt-2 text-sm text-slate-500">Due: {new Date(assignment.due_at).toLocaleString()}</p>
                      </div>
                      {assignment.attachment ? (
                        <Button type="button" variant="outline" className="rounded-sm border-[#002147]/15 bg-white hover:bg-[#f4efe5]" onClick={() => downloadProtectedFile(assignment.attachment.download_url, assignment.attachment.name)} data-testid={`student-assignment-download-${assignment.id}`}>
                          <Download className="mr-2 h-4 w-4" /> Download Task
                        </Button>
                      ) : null}
                    </div>

                    {!submission || submission.status === "submitted" ? (
                      <div className="border border-[#002147]/10 bg-[#fcfbf8] p-5" data-testid={`student-assignment-upload-area-${assignment.id}`}>
                        <p className="mb-3 font-medium text-[#002147]">Upload your work</p>
                        <Input type="file" multiple onChange={(event) => setUploadMap({ ...uploadMap, [assignment.id]: event.target.files })} data-testid={`student-assignment-file-input-${assignment.id}`} />
                        <div className="mt-4 flex flex-wrap gap-3">
                          <Button type="button" className="rounded-sm bg-[#002147] text-white hover:bg-[#001530]" onClick={() => handleAssignmentUpload(assignment.id)} data-testid={`student-assignment-submit-button-${assignment.id}`}>
                            <FileUp className="mr-2 h-4 w-4" /> {submission ? "Replace Submission" : "Submit Assignment"}
                          </Button>
                          {submission?.files?.map((file) => (
                            <Button key={file.id} type="button" variant="outline" className="rounded-sm border-[#002147]/15 bg-white hover:bg-[#f4efe5]" onClick={() => downloadProtectedFile(file.download_url, file.name)} data-testid={`student-submission-file-${assignment.id}-${file.id}`}>
                              <Download className="mr-2 h-4 w-4" /> {file.name}
                            </Button>
                          ))}
                        </div>
                        {submission && submission.status === "submitted" ? <p className="mt-4 text-sm text-amber-700">Submitted and awaiting review.</p> : null}
                      </div>
                    ) : null}

                    {reviewed ? (
                      <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr]" data-testid={`student-assignment-review-${assignment.id}`}>
                        <Card className="section-frame rounded-sm bg-[#002147] text-white shadow-none">
                          <CardContent className="space-y-4 p-6">
                            <p className="text-xs uppercase tracking-[0.18em] text-[#f3d8a1]">Result</p>
                            <h3 className="font-display text-3xl">{submission.review.score} / {submission.review.max_score}</h3>
                            <p className="text-sm text-white/82">Percentile: {submission.review.stats.percentile}% · Cohort average: {submission.review.stats.average}</p>
                            <div className="h-52" data-testid={`student-assignment-chart-${assignment.id}`}>
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData}>
                                  <XAxis dataKey="name" stroke="#dbeafe" tick={{ fontSize: 11 }} />
                                  <YAxis stroke="#dbeafe" tick={{ fontSize: 11 }} />
                                  <Tooltip />
                                  <Bar dataKey="value" fill="#c5a059" radius={[0, 0, 0, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          </CardContent>
                        </Card>

                        <div className="grid gap-4">
                          <Card className="section-frame rounded-sm bg-white shadow-none">
                            <CardContent className="space-y-3 p-6">
                              <h3 className="font-display text-2xl text-[#002147]">Questions to review</h3>
                              <ul className="space-y-2" data-testid={`student-assignment-incorrect-${assignment.id}`}>
                                {submission.review.incorrect_questions.map((item) => <li key={item} className="text-sm leading-7 text-slate-700">• {item}</li>)}
                              </ul>
                            </CardContent>
                          </Card>
                          <Card className="section-frame rounded-sm bg-white shadow-none">
                            <CardContent className="space-y-3 p-6">
                              <h3 className="font-display text-2xl text-[#002147]">Marks lost</h3>
                              <ul className="space-y-2" data-testid={`student-assignment-marks-lost-${assignment.id}`}>
                                {submission.review.marks_lost.map((item) => <li key={item} className="text-sm leading-7 text-slate-700">• {item}</li>)}
                              </ul>
                            </CardContent>
                          </Card>
                          <Card className="section-frame rounded-sm bg-white shadow-none">
                            <CardContent className="space-y-3 p-6">
                              <h3 className="font-display text-2xl text-[#002147]">Worked solution and feedback</h3>
                              <p className="text-sm leading-7 text-slate-700">{submission.review.worked_solution}</p>
                              {submission.review.ai_feedback ? <p className="border-t border-[#002147]/10 pt-3 text-sm leading-7 text-slate-700">{submission.review.ai_feedback}</p> : null}
                            </CardContent>
                          </Card>
                        </div>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : null}

        {activeTab === "AI Helper" ? (
          <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]" data-testid="student-ai-section">
            <Card className="section-frame rounded-sm bg-white shadow-none">
              <CardContent className="space-y-4 p-6">
                <p className="eyebrow text-[#002147]/60">Ascend Academy AI</p>
                <h2 className="font-display text-3xl text-[#002147]">Ask for an immediate explanation.</h2>
                <p className="text-sm leading-7 text-slate-700">Use the AI helper for quick clarification on coursework, methods, specialist questions, or exam technique. It is designed to explain clearly and point you toward the next step to practise.</p>
                <Textarea value={aiPrompt} onChange={(event) => setAiPrompt(event.target.value)} rows={8} placeholder="Ask your question here" data-testid="student-ai-prompt-input" />
                <Button onClick={handleAskAi} disabled={aiLoading} className="rounded-sm bg-[#002147] text-white hover:bg-[#001530]" data-testid="student-ai-submit-button">
                  <BrainCircuit className="mr-2 h-4 w-4" /> {aiLoading ? "Thinking..." : "Get Explanation"}
                </Button>
              </CardContent>
            </Card>

            <Card className="section-frame rounded-sm bg-[#002147] text-white shadow-none" data-testid="student-ai-answer-card">
              <CardContent className="space-y-4 p-6">
                <p className="eyebrow text-[#f3d8a1]">AI response</p>
                <div className="min-h-[320px] whitespace-pre-wrap text-sm leading-7 text-white/85" data-testid="student-ai-answer-text">
                  {aiAnswer || "Your explanation will appear here once you submit a question."}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : null}
      </div>
    </div>
  );
};